<div class="container-fluid" style="max-width: 1200px;">
    <h1 class="m-3 display-6 vm-container-header">
        <i class="bi bi-cloud-upload me-2"></i>Master Data Loads
    </h1>

    <div class="m-3">
        <select (change)="onCustomerChange($event)" aria-label="Select a customer" class="form-select">
            <option value="">Select a Customer</option>
            @for (customer of customers; track customer.id) {
                <option [value]="customer.id">{{ customer.name }}</option>
            }
        </select>
    </div>

    @if (customerId) {
        <!-- Upload bundle -->
        <div class="m-3 p-3 bg-body-tertiary rounded-3 shadow-sm">
            <div class="d-flex align-items-center justify-content-between mb-3">
                <h2 class="h5 m-0">Upload a bundle</h2>
                <button class="btn btn-outline-info" type="button" (click)="openHelp()">
                    <i class="bi bi-question-circle me-2"></i>Help &amp; file formats
                </button>
            </div>
            <div class="row g-3">
                @for (kind of fileKinds; track kind.key) {
                    <div class="col-md-6">
                        <label class="form-label" [attr.for]="'file-' + kind.key">{{ kind.label }}</label>
                        <input #fileInput class="form-control" type="file" accept=".txt"
                               [id]="'file-' + kind.key"
                               (change)="onFileSelected(kind.key, $event)">
                    </div>
                }
            </div>

            <div class="mt-3 d-flex gap-2">
                <button class="btn btn-primary" type="button"
                        [disabled]="uploading || !hasSelectedFiles"
                        (click)="onUpload()">
                    <i class="bi bi-upload me-2"></i>Upload &amp; Validate
                </button>
                <button class="btn btn-outline-primary" type="button"
                        [disabled]="uploading || !hasSelectedFiles"
                        (click)="onReset()">
                    <i class="bi bi-x-circle me-2"></i>Reset
                </button>
            </div>
        </div>

        <!-- Inline rejection report -->
        @if (rejectionReport) {
            <div class="m-3 p-3 alert callout callout-danger border-danger">
                <h2 class="h5 mb-3">
                    <i class="bi bi-exclamation-circle-fill text-danger me-2"></i>
                    Bundle rejected — {{ rejectionReport.error_count }} error(s), {{ rejectionReport.warning_count }} warning(s)
                </h2>
                <table class="table table-sm table-hover table-st table-striped mt-2 mb-0">
                    <thead>
                    <tr>
                        <th>File</th>
                        <th>Row</th>
                        <th>Column</th>
                        <th>Code</th>
                        <th>Message</th>
                        <th>Severity</th>
                    </tr>
                    </thead>
                    <tbody class="table-group-divider">
                        @for (finding of rejectionReport.findings; track $index) {
                            <tr>
                                <td>{{ this.fileKindLabel(finding.file_kind) }}</td>
                                <td>{{ finding.row_number ?? '—' }}</td>
                                <td>{{ finding.column ?? '—' }}</td>
                                <td><code>{{ finding.code }}</code></td>
                                <td>{{ finding.message }}</td>
                                <td>{{ finding.severity }}</td>
                            </tr>
                        }
                    </tbody>
                </table>
            </div>
        }

        <!-- Run history -->
        <div class="m-3 p-3 shadow-sm">
            <h2 class="h5 mb-3">Run history</h2>
            @if (((service.total$ | async) ?? 0) > 0) {
                <table class="table table-striped table-hover">
                    <thead>
                    <tr>
                        <th sortable="status" (sort)="onSort($event)">Status</th>
                        <th>Files</th>
                        <th sortable="triggeredByName" (sort)="onSort($event)">Uploaded by</th>
                        <th sortable="createdAt" (sort)="onSort($event)">Load Time</th>
                        <th class="text-end"># Loaded</th>
                        <th class="text-end"># Skipped</th>
                    </tr>
                    </thead>
                    <tbody class="table-group-divider">
                        @for (run of service.data$ | async; track run.id) {
                            <tr class="align-middle" role="button" (click)="onViewDetail(run)">
                                <td><span class="badge" [class]="statusBadgeClass(run.status)">{{ run.status }}</span></td>
                                <td>
                                    @for (kind of runFileKindChips(run.fileKinds); track kind.key) {
                                        <span class="badge rounded-pill bg-secondary text-dark me-1"
                                              [title]="kind.label">{{ kind.abbr }}</span>
                                    } @empty {
                                        <span class="text-muted">—</span>
                                    }
                                </td>
                                <td>{{ run.triggeredByName ?? '—' }}</td>
                                <td>{{ this.longFormatDate(run.createdAt) }}</td>
                                <td class="text-end">{{ run.totalRowsLoaded }}</td>
                                <td class="text-end">{{ run.totalRowsSkipped }}</td>
                            </tr>
                        }
                    </tbody>
                </table>

                <div class="d-flex justify-content-between">
                    <ngb-pagination [collectionSize]="(service.total$ | async) ?? 0" [(page)]="service.page"
                                    [pageSize]="service.pageSize" [maxSize]="10" [rotate]="true" [ellipses]="true"/>
                    <select [(ngModel)]="this.service.pageSize" class="form-select" name="pageSize" style="width: auto">
                        @for (element of this.globalService.pagination; track element) {
                            <option [ngValue]="element">
                                {{ element }} items per page
                            </option>
                        }
                    </select>
                </div>
            }
            @else {
                <div class="card bg-info-subtle border border-info rounded p-3">
                    No loads yet for this customer.
                </div>
            }
        </div>
    }
</div>

<!-- Detail modal -->
<ng-template #detailModal let-modal>
    @if (selectedDetail; as detail) {
        <div class="modal-header">
            <h5 class="modal-title">
                <span class="badge me-2" [class]="statusBadgeClass(detail.status)">{{ detail.status }}</span>
                Load detail > {{ customerName(detail.customerId) }}
            </h5>
            <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss()"></button>
        </div>
        <div class="modal-body">
            <dl class="row mb-3">
                <dt class="col-sm-2">Uploaded by</dt>
                <dd class="col-sm-10">{{ detail.triggeredByName ?? '—' }}</dd>
                <dt class="col-sm-2">Load Time</dt>
                <dd class="col-sm-10">{{ this.longFormatDate(detail.createdAt) }}</dd>
            </dl>

            @if (detail.failureSummary) {
                <div class="alert"
                     [class.alert-danger]="detail.status === 'FAILED'"
                     [class.alert-warning]="detail.status !== 'FAILED'">
                    {{ detail.failureSummary }}
                    @if (detail.status === 'FAILED') {
                        <div class="small mt-1">Re-uploading the same bundle is safe — loads are insert-only and skip existing rows.</div>
                    }
                </div>
            }

            @if (detailViews.length > 0) {
                <ul ngbNav #nav="ngbNav" [(activeId)]="activeTabId" class="nav-tabs">
                    @for (view of detailViews; track view.file.id; let i = $index) {
                        <li [ngbNavItem]="i">
                            <a ngbNavLink>
                                {{ fileKindLabel(view.file.fileKind) }}
                                @if (view.findings.length > 0) {
                                    <span class="badge rounded-pill bg-secondary text-dark ms-1">{{ view.findings.length }}</span>
                                }
                            </a>
                            <ng-template ngbNavContent>
                                <div class="mb-2">
                                    <i class="bi bi-file-earmark me-2"></i><code class="me-2">{{ view.file.originalFilename }}</code>
                                    <span class="badge rounded-pill bg-success mx-2">Loaded {{ view.file.rowsLoaded }}</span>
                                    <span class="badge rounded-pill bg-dark">Skipped {{ view.file.rowsSkipped }}</span>
                                </div>

                                @if (view.findings.length > 0) {
                                    <table class="table table-sm mb-2">
                                        <thead>
                                        <tr>
                                            <th>Row</th>
                                            <th>Column</th>
                                            <th>Code</th>
                                            <th>Message</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                            @for (finding of pagedFindings(view); track $index) {
                                                <tr>
                                                    <td>{{ finding.row_number ?? '—' }}</td>
                                                    <td>{{ finding.column ?? '—' }}</td>
                                                    <td><code>{{ finding.code }}</code></td>
                                                    <td>{{ finding.message }}</td>
                                                </tr>
                                            }
                                        </tbody>
                                    </table>

                                    @if (view.findings.length > detailPageSize) {
                                        <div class="d-flex justify-content-between p-2">
                                            <ngb-pagination [collectionSize]="view.findings.length" [(page)]="view.page"
                                                            [pageSize]="detailPageSize" [maxSize]="10" [rotate]="true" [ellipses]="true"/>

                                            <select [(ngModel)]="detailPageSize" (ngModelChange)="resetDetailPages()"
                                                    class="form-select" name="detailPageSize" style="width: auto">
                                                @for (size of globalService.pagination; track size) {
                                                    <option [ngValue]="size">
                                                        {{ size }} items per page
                                                    </option>
                                                }
                                            </select>
                                        </div>
                                    }
                                }
                                @else {
                                    <p class="alert callout callout-success text-muted mb-0">
                                        <i class="bi bi-check-circle me-2"></i>All rows loaded successfully
                                    </p>
                                }
                            </ng-template>
                        </li>
                    }
                </ul>
                <div [ngbNavOutlet]="nav" class="mt-3"></div>
            } @else {
                <p class="text-muted">No per-file detail recorded yet.</p>
            }
        </div>
        <div class="modal-footer">
            <button type="button" class="btn btn-secondary" (click)="modal.close()">Close</button>
        </div>
    }
</ng-template>

<!-- Help modal -->
<ng-template #helpModal let-modal>
    <div class="modal-header">
        <h4 class="modal-title">
            <i class="bi bi-question-circle me-2"></i>Master Data Loads > Help
        </h4>
        <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss()"></button>
    </div>
    <div class="modal-body">
        <h5>How a load works</h5>
        <ol class="mb-4">
            <li>Attach one file per kind (at most one each). A bundle can be any
                subset of the five kinds &mdash; you don't have to upload all of them.</li>
            <li>Click <strong>Upload &amp; Validate</strong>. The bundle is validated synchronously and, if any
                file fails, the whole bundle is <strong>rejected</strong> with a report &mdash; nothing is loaded.</li>
            <li>A valid bundle is <strong>queued</strong> and processed in the background. The run appears in
                Run history and updates from <code>QUEUED</code> &rarr; <code>PROCESSING</code> &rarr; a terminal
                status on its own.</li>
            <li>Loads are <strong>insert-only and skip rows that already exist</strong>, so re-uploading the same
                bundle is safe &mdash; existing rows are counted as skipped, not duplicated.</li>
        </ol>

        <div class="callout callout-info small mb-4">
            All files are <strong>tab-delimited</strong> <code>.txt</code> files with a header row. Column names and
            their order must match exactly (optional columns must still be present as headers; their values may be
            blank). A <strong>Distribution Centers</strong> file requires a <strong>Trading Partners</strong> file in
            the same bundle.
        </div>

        <h5>File kinds</h5>
        @for (kind of fileKinds; track kind.key) {
            <div class="card mb-3">
                <div class="card-header d-flex justify-content-between align-items-center">
                    <span>
                        <strong>{{ kind.label }}</strong>
                        <span class="small text-muted ms-2">{{ fileGuides[kind.key].columns.length }} columns</span>
                    </span>
                    <button class="btn btn-sm btn-outline-primary" type="button" (click)="downloadSample(kind.key)">
                        <i class="bi bi-download me-2"></i>Download sample
                    </button>
                </div>
                <div class="card-body">
                    <p class="text-muted small">{{ fileGuides[kind.key].description }}</p>
                    <div class="table-responsive">
                        <table class="table table-sm table-bordered small mb-0">
                            <thead>
                            <tr>
                                @for (col of fileGuides[kind.key].columns; track col) {
                                    <th class="text-nowrap">{{ col }}</th>
                                }
                            </tr>
                            </thead>
                            <tbody>
                                @for (row of fileGuides[kind.key].rows; track $index) {
                                    <tr>
                                        @for (col of fileGuides[kind.key].columns; track col; let c = $index) {
                                            <td class="text-nowrap">{{ row[c] || '' }}</td>
                                        }
                                    </tr>
                                }
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        }
    </div>
    <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="modal.close()">Close</button>
    </div>
</ng-template>
